import React from 'react';
import styled from '@benzinga/themetron';
import {
  NewsTitleAuthor,
  ContentFeedProps,
  FeaturedCard,
  NewsContainer,
  ContentFeed,
  BenzingaBriefNewsElement,
  EdgePortfolioNewsCard,
} from '@benzinga/news';
import type { DeviceType } from '@benzinga/device-utils';
import { LinkGroupBlock } from './LinkGroupBlock';

interface ContentFeedBlockProps {
  feed: ContentFeedProps;
  attrs: {
    className?: string;
    data: {
      contentFeedProps?: ContentFeedProps;
      mobileOnly?: boolean;
      newsItemElement: 'featured_card' | 'nano' | 'briefs_element' | 'title' | 'full_card';
      newsContainer: 'none' | 'box';
      layout?: 'feed';
      briefs_type?: 'briefs' | 'exclusive_briefs';
      preview_text_selection?: 'body' | 'teaser';
    };
  };
  deviceType: DeviceType;
}

export const ContentFeedBlock: React.FC<ContentFeedBlockProps> = ({ attrs, deviceType, feed }) => {
  if (attrs?.data?.mobileOnly && deviceType === 'desktop') {
    return null;
  }

  if (feed) {
    return <ContentFeed {...feed} />;
  }

  const contentFeedProps = attrs?.data?.contentFeedProps;

  if (contentFeedProps) {
    if (attrs?.className) {
      contentFeedProps.className = attrs.className;
    }
    if (attrs?.data?.newsItemElement === 'featured_card') {
      const layout = attrs?.data?.layout;
      contentFeedProps.newsItemElement = (node, index) => (
        <FeaturedCard
          imagePreload={index < 6}
          imageWidth={deviceType === 'mobile' ? 120 : 240}
          key={`item-${index}`}
          layout={layout || 'default'}
          node={node}
          showCommentsIcon={contentFeedProps?.postCardProps?.showCommentsIcon}
        />
      );
    } else if (attrs?.data?.newsItemElement === 'briefs_element') {
      contentFeedProps.newsItemElement = (node, index) => {
        return (
          <BenzingaBriefNewsElement
            data={node}
            key={index}
            previewTextSelection={attrs?.data?.preview_text_selection}
            showCommentsIcon={contentFeedProps?.postCardProps?.showCommentsIcon}
          />
        );
      };
    } else if (attrs?.data?.newsItemElement === 'nano') {
      contentFeedProps.newsItemElement = (node, index) => {
        return <NewsTitleAuthor key={index} node={node} />;
      };
    } else if (attrs?.data?.newsItemElement === 'full_card') {
      contentFeedProps.newsItemElement = (node, index) => {
        return (
          <EdgePortfolioNewsCard
            imagePreload={index < 6}
            imageWidth={deviceType === 'mobile' ? 120 : 240}
            key={`item-${index}`}
            node={node}
            showCommentsIcon={contentFeedProps?.postCardProps?.showCommentsIcon}
          />
        );
      };
    } else if (attrs?.data?.newsItemElement === 'title') {
      const links = contentFeedProps.nodes?.map(({ created, title, url }) => ({ created, title, url }));
      const title = contentFeedProps?.title ?? '';

      if (links && links.length > 0) {
        return (
          <LinkGroupBlock
            attrs={{
              data: {
                highlightLatest: true,
                links: links,
                maxLinesLimit: 5,
                title: title,
                variant: 'secondary',
              },
            }}
          />
        );
      }
    }
  }

  if (attrs.data.newsContainer === 'box') {
    return (
      <NewsContainer title={contentFeedProps?.title ?? ''}>
        <ContentFeed {...contentFeedProps} showLoadMoreButton={true} title={undefined} />
      </NewsContainer>
    );
  } else {
    return (
      <Container>
        <ContentFeed {...contentFeedProps} />
      </Container>
    );
  }
};

const Container = styled.div`
  .featured-card {
    color: #000000;
    .post-card {
      margin-bottom: 12px;
    }
  }
  .feed-more-wrapper {
    height: 100px;
  }
  .benzinga-brief-block {
    padding: 20px 0;
    border-bottom: 1px solid #ceddf2;
  }
`;
